import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { TCOLogo } from '../components/ui/TCOLogo'
import { useAuth } from '../providers/AuthProvider'
import type { CreateTenantRequest } from '@tco/shared-types'

interface RegisterFormData {
  name: string
  inn: string
  domain: string
  admin_email: string
  admin_password: string
  password_confirm: string
  admin_phone: string
  agree_to_policy: boolean
}

interface ValidationErrors {
  name?: string
  inn?: string
  domain?: string
  admin_email?: string
  admin_password?: string
  password_confirm?: string
  agree_to_policy?: string
}

export function Register() {
  const navigate = useNavigate()
  const { register } = useAuth()

  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    inn: '',
    domain: '',
    admin_email: '',
    admin_password: '',
    password_confirm: '',
    admin_phone: '',
    agree_to_policy: false,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  // Live валидация субдомена при вводе
  useEffect(() => {
    if (formData.domain) {
      const domainLower = formData.domain.toLowerCase()
      if (formData.domain !== domainLower) {
        setFormData(prev => ({ ...prev, domain: domainLower }))
      }
    }
  }, [formData.domain])

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {}

    // Название организации
    if (!formData.name.trim()) {
      errors.name = 'Название организации обязательно'
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Название должно содержать минимум 2 символа'
    } else if (formData.name.length > 255) {
      errors.name = 'Название не должно превышать 255 символов'
    }

    // ИНН
    if (!formData.inn) {
      errors.inn = 'ИНН обязателен'
    } else if (!/^\d{12}$/.test(formData.inn)) {
      errors.inn = 'ИНН должен состоять из 12 цифр'
    }

    // Субдомен
    if (!formData.domain) {
      errors.domain = 'Субдомен обязателен'
    } else if (formData.domain.length < 3) {
      errors.domain = 'Субдомен должен содержать минимум 3 символа'
    } else if (formData.domain.length > 63) {
      errors.domain = 'Субдомен не должен превышать 63 символа'
    } else if (!/^[a-z0-9-]+$/.test(formData.domain)) {
      errors.domain = 'Субдомен может содержать только строчные латинские буквы, цифры и дефис'
    }

    // Email
    if (!formData.admin_email) {
      errors.admin_email = 'Email обязателен'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      errors.admin_email = 'Некорректный формат email'
    }

    // Пароль
    if (!formData.admin_password) {
      errors.admin_password = 'Пароль обязателен'
    } else if (formData.admin_password.length < 8) {
      errors.admin_password = 'Пароль должен содержать минимум 8 символов'
    }

    // Подтверждение пароля
    if (!formData.password_confirm) {
      errors.password_confirm = 'Подтвердите пароль'
    } else if (formData.admin_password !== formData.password_confirm) {
      errors.password_confirm = 'Пароли не совпадают'
    }

    // Согласие с политикой
    if (!formData.agree_to_policy) {
      errors.agree_to_policy = 'Необходимо согласие с политикой конфиденциальности'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const requestData: CreateTenantRequest = {
        name: formData.name.trim(),
        inn: formData.inn,
        domain: formData.domain,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        admin_phone: formData.admin_phone || undefined,
      }

      // Вызов метода register из AuthProvider
      await register(requestData)

      // Редирект на дашборд после успешной регистрации
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))

    // Очистить ошибку валидации для этого поля
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name as keyof ValidationErrors]
        return newErrors
      })
    }
  }

  const passwordStrength = formData.admin_password.length >= 8 ? 'strong' : 'weak'
  const passwordsMatch = formData.admin_password && formData.password_confirm && formData.admin_password === formData.password_confirm

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex flex-col items-center">
          <TCOLogo variant="full" colorScheme="blue" width={320} />
          <p className="mt-6 text-sm text-gray-600 text-center">
            Зарегистрируйте вашу организацию в системе TCO
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Регистрация организации</CardTitle>
            <CardDescription>
              Заполните форму для создания аккаунта вашей организации. Вы получите 14 дней пробного периода.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Название организации */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Название организации <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border ${validationErrors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  placeholder='ТОО "Ваша Компания"'
                />
                {validationErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              {/* ИНН */}
              <div>
                <label htmlFor="inn" className="block text-sm font-medium text-gray-700">
                  ИНН организации (12 цифр) <span className="text-red-500">*</span>
                </label>
                <input
                  id="inn"
                  name="inn"
                  type="text"
                  required
                  maxLength={12}
                  value={formData.inn}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border ${validationErrors.inn ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono`}
                  placeholder="123456789012"
                />
                {validationErrors.inn && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.inn}</p>
                )}
              </div>

              {/* Субдомен */}
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700">
                  Субдомен <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <input
                    id="domain"
                    name="domain"
                    type="text"
                    required
                    value={formData.domain}
                    onChange={handleInputChange}
                    className={`block w-full px-3 py-2 border ${validationErrors.domain ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono`}
                    placeholder="yourcompany"
                  />
                  {formData.domain && (
                    <p className="mt-1 text-xs text-gray-500">
                      Ваш адрес: <span className="font-mono font-medium">{formData.domain}.timecontroloffice.kz</span>
                    </p>
                  )}
                </div>
                {validationErrors.domain && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.domain}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Только строчные латинские буквы, цифры и дефис
                </p>
              </div>

              {/* Email администратора */}
              <div>
                <label htmlFor="admin_email" className="block text-sm font-medium text-gray-700">
                  Email администратора <span className="text-red-500">*</span>
                </label>
                <input
                  id="admin_email"
                  name="admin_email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.admin_email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border ${validationErrors.admin_email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  placeholder="admin@example.com"
                />
                {validationErrors.admin_email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.admin_email}</p>
                )}
              </div>

              {/* Пароль */}
              <div>
                <label htmlFor="admin_password" className="block text-sm font-medium text-gray-700">
                  Пароль <span className="text-red-500">*</span>
                </label>
                <input
                  id="admin_password"
                  name="admin_password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.admin_password}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border ${validationErrors.admin_password ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  placeholder="••••••••"
                />
                {validationErrors.admin_password && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.admin_password}</p>
                )}
                {formData.admin_password && (
                  <p className={`mt-1 text-xs ${passwordStrength === 'strong' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {passwordStrength === 'strong' ? '✓ Надежный пароль' : '⚠ Минимум 8 символов'}
                  </p>
                )}
              </div>

              {/* Подтверждение пароля */}
              <div>
                <label htmlFor="password_confirm" className="block text-sm font-medium text-gray-700">
                  Подтвердите пароль <span className="text-red-500">*</span>
                </label>
                <input
                  id="password_confirm"
                  name="password_confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password_confirm}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border ${validationErrors.password_confirm ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  placeholder="••••••••"
                />
                {validationErrors.password_confirm && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.password_confirm}</p>
                )}
                {passwordsMatch && (
                  <p className="mt-1 text-xs text-green-600">✓ Пароли совпадают</p>
                )}
              </div>

              {/* Телефон (опционально) */}
              <div>
                <label htmlFor="admin_phone" className="block text-sm font-medium text-gray-700">
                  Телефон администратора
                </label>
                <input
                  id="admin_phone"
                  name="admin_phone"
                  type="tel"
                  autoComplete="tel"
                  value={formData.admin_phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="+77011234567"
                />
              </div>

              {/* Согласие с политикой */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="agree_to_policy"
                    name="agree_to_policy"
                    type="checkbox"
                    checked={formData.agree_to_policy}
                    onChange={handleInputChange}
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="agree_to_policy" className="font-medium text-gray-700">
                    Я согласен с{' '}
                    <a href="https://timecontroloffice.kz/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark underline">
                      политикой конфиденциальности
                    </a>{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  {validationErrors.agree_to_policy && (
                    <p className="mt-1 text-red-600">{validationErrors.agree_to_policy}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                variant="tco"
              >
                {isLoading ? 'Регистрация...' : 'Зарегистрировать организацию'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                Уже есть аккаунт?{' '}
                <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
                  Войти
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-gray-500">
          <p>После регистрации вы получите 14 дней бесплатного пробного периода</p>
        </div>
      </div>
    </div>
  )
}
